import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Envelope, LockSimple, User, CircleNotch } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/Logo";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    const res = await register(form.name, form.email, form.password);
    setLoading(false);
    if (res.ok) navigate("/", { replace: true });
    else setError(res.error);
  };

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-6 py-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md border border-cyan/20 bg-navy-surface p-8 sm:p-10"
        data-testid="register-page"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <BrandMark className="h-14 w-14" />
          <h1 className="mt-5 font-heading text-2xl font-black tracking-tight text-white">Create account</h1>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-teal">
            Join QuadHelix Labs
          </p>
        </div>

        {error && (
          <div data-testid="register-error" className="mb-5 border-l-2 border-destructive bg-destructive/10 px-4 py-2.5 font-mono text-xs text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-teal">Name / Institution</label>
            <div className="relative">
              <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-teal" />
              <input
                data-testid="register-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Dr. Jane Researcher"
                className="w-full border border-cyan/15 bg-navy py-3 pl-9 pr-3 font-mono text-sm text-white placeholder:text-teal/40 outline-none focus:border-cyan/50"
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-teal">Email</label>
            <div className="relative">
              <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-teal" />
              <input
                data-testid="register-email-input"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                placeholder="you@lab.com"
                className="w-full border border-cyan/15 bg-navy py-3 pl-9 pr-3 font-mono text-sm text-white placeholder:text-teal/40 outline-none focus:border-cyan/50"
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-teal">Password</label>
            <div className="relative">
              <LockSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-teal" />
              <input
                data-testid="register-password-input"
                type="password"
                required
                value={form.password}
                onChange={set("password")}
                placeholder="At least 6 characters"
                className="w-full border border-cyan/15 bg-navy py-3 pl-9 pr-3 font-mono text-sm text-white placeholder:text-teal/40 outline-none focus:border-cyan/50"
              />
            </div>
          </div>
          <button
            data-testid="register-submit-button"
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 bg-cyan py-3.5 font-mono text-sm font-semibold uppercase tracking-wider text-navy-deep transition-all hover:bg-cyan-bright hover:shadow-glow disabled:opacity-60"
          >
            {loading ? <CircleNotch size={18} className="animate-spin" /> : "Create Account"}
          </button>
        </form>

        <p className="mt-6 text-center font-mono text-xs text-teal-muted">
          Already registered?{" "}
          <Link to="/login" data-testid="go-to-login" className="text-cyan hover:text-cyan-bright">
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
